import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import ProcessSection from "@/sections/ProcessSection";
import CTASection from "@/components/CTASection";
import { Lightbulb, PenTool, Code2, Rocket } from "lucide-react";

const pillars = [
  { icon: Lightbulb, title: "Strategy", desc: "We start with your business goals and shape the right product around them." },
  { icon: PenTool, title: "Design", desc: "Clean, modern interfaces designed around your brand and your users." },
  { icon: Code2, title: "Engineering", desc: "Secure, scalable code built to perform today and grow tomorrow." },
  { icon: Rocket, title: "Product Development", desc: "End-to-end ownership — from first sketch to launch and beyond." },
];

const About = () => (
  <>
    <Seo
      title="About Us | Kryvo Technologies"
      description="Kryvo Technologies combines strategy, design, engineering and product development to transform business ideas into modern digital products."
    />

    <section className="grid-bg bg-kryvo-mist pb-16 pt-[calc(72px+4rem)] sm:pb-20" data-testid="about-hero">
      <div className="container-x max-w-3xl">
        <Reveal>
          <span className="eyebrow">
            <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
            About Kryvo
          </span>
          <h1 className="mt-4 text-balance text-4xl font-semibold text-kryvo-ink sm:text-5xl">
            Technology Built Around Your Business.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-kryvo-ink/65 sm:text-lg">
            Kryvo Technologies is a digital product development company. We work with businesses to
            transform ideas, workflows and opportunities into modern digital products — from websites
            and web applications to AI-powered MVPs and mobile apps.
          </p>
          <p className="mt-4 text-base leading-relaxed text-kryvo-ink/65 sm:text-lg">
            We also build our own SaaS products — Kryvo CRM and Kryvo Engage — so we understand what
            it takes to design, launch and grow software from both sides of the table.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="container-x py-16 sm:py-24" data-testid="about-pillars">
      <SectionHeader
        eyebrow="What We Bring"
        title="One team, four disciplines"
        description="Every Kryvo engagement brings together the disciplines a great digital product needs."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="group h-full rounded-2xl border border-kryvo-green/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/30 hover:shadow-[0_20px_45px_-22px_rgba(6,78,42,0.3)]">
              <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green transition-colors duration-300 group-hover:bg-kryvo-green group-hover:text-white">
                <p.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="text-xl font-semibold text-kryvo-ink">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-kryvo-ink/60">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <ProcessSection />
    <CTASection />
  </>
);

export default About;
